import { useTranslation } from 'react-i18next';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

interface DataGridPaginationProps {
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  total: number;
  pageCount: number;
  isFetching?: boolean;
  pageSizeOptions?: number[];
}

/**
 * Pager, length menu and record counter, in the DataTables layout the design
 * styles: `dataTables_length` / `dataTables_info` / `dataTables_paginate` are
 * the class names `stla_datatables.css` and `dataTables.bootstrap5` target.
 */
export function DataGridPagination({
  pagination,
  onPaginationChange,
  total,
  pageCount,
  isFetching = false,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
}: DataGridPaginationProps) {
  const { t } = useTranslation();

  const { pageIndex, pageSize } = pagination;
  const firstRow = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min(total, (pageIndex + 1) * pageSize);
  const canPrevious = pageIndex > 0;
  const canNext = pageIndex + 1 < pageCount;

  const goToPage = (index: number) => {
    onPaginationChange({ pageIndex: index, pageSize });
  };

  // A window of at most five page links around the current page, so a
  // hundred-thousand-row grid does not render a thousand buttons.
  const windowStart = Math.max(0, Math.min(pageIndex - 2, pageCount - 5));
  const pageWindow = Array.from(
    { length: Math.min(5, Math.max(pageCount, 0)) },
    (_, offset) => windowStart + offset,
  ).filter((index) => index >= 0 && index < pageCount);

  return (
    <div className="row align-items-center mt-2">
      <div className="col-sm-12 col-md-3">
        <div className="dataTables_length">
          <label className="d-flex align-items-center gap-2 mb-0">
            <select
              className="form-select form-select-sm w-auto"
              value={pageSize}
              onChange={(event) => {
                // Back to the first page: the row that was at the top of page 7
                // of 10-row pages is not on page 7 of 100-row pages.
                onPaginationChange({ pageIndex: 0, pageSize: Number(event.target.value) });
              }}
              aria-label={t('RowsPerPage', 'Rows per page')}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="fs-sm text-muted">{t('RowsPerPage', 'Rows per page')}</span>
          </label>
        </div>
      </div>

      <div className="col-sm-12 col-md-4">
        <div className="dataTables_info fs-sm text-muted" role="status" aria-live="polite">
          {t('ShowingEntries', 'Showing {{first}} to {{last}} of {{total}} entries', {
            first: firstRow,
            last: lastRow,
            total,
          })}
          {isFetching ? (
            <span className="spinner-border spinner-border-sm ms-2 align-middle" role="status" />
          ) : null}
        </div>
      </div>

      <div className="col-sm-12 col-md-5">
        <div className="dataTables_paginate paging_simple_numbers">
          <ul className="pagination pagination-sm justify-content-md-end mb-0">
            <li className={`paginate_button page-item previous ${canPrevious ? '' : 'disabled'}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => goToPage(pageIndex - 1)}
                disabled={!canPrevious}
              >
                {t('Previous', 'Previous')}
              </button>
            </li>

            {pageWindow.map((index) => (
              <li
                key={index}
                className={`paginate_button page-item ${index === pageIndex ? 'active' : ''}`}
              >
                <button type="button" className="page-link" onClick={() => goToPage(index)}>
                  {index + 1}
                </button>
              </li>
            ))}

            <li className={`paginate_button page-item next ${canNext ? '' : 'disabled'}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => goToPage(pageIndex + 1)}
                disabled={!canNext}
              >
                {t('Next', 'Next')}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
